import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type SyntheticEvent,
} from 'react'
import { Check, X } from 'lucide-react'
import { type ApInvoiceAssignedUser } from '@/features/invoices/types'
import { useStaff } from '@/api/hooks'
import { useCloseAfterPress } from '@/components/use-close-after-press'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { inlineEditKey, inlineTextareaKey } from '@/components/inline-edit-keys'
import { SearchInput } from '@/components/search-input'

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function fieldValue(value: unknown) {
  if (value === null || value === undefined || value === '') return 'Not set'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

type EditableFieldProps = {
  label: string
  value: string
  displayValue?: string
  type?: 'text' | 'date' | 'number' | 'textarea' | 'select'
  options?: { value: string; label: string }[]
  saving?: boolean
  onConfirm: (value: string, done: () => void) => void
}

type SummaryReadOnlyRowProps = {
  label: string
  children: ReactNode
}

export function SummaryReadOnlyRow({ label, children }: SummaryReadOnlyRowProps) {
  return (
    <div className="grid min-w-0 gap-1 border-b px-3 py-2 last:border-b-0 sm:grid-cols-[minmax(8rem,0.42fr)_minmax(0,1fr)] sm:items-start sm:gap-3">
      <span className="pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 px-1.5 py-0.5 text-sm font-medium">{children}</span>
    </div>
  )
}

type InlineInvoiceFieldProps = EditableFieldProps & {
  className?: string
}

export function InlineInvoiceField({
  label,
  value,
  displayValue,
  type = 'text',
  options,
  saving,
  onConfirm,
  className,
}: InlineInvoiceFieldProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  const startEditing = () => {
    setDraft(value)
    setEditing(true)
  }

  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }

  // A field blurred by a press closes after the press's click (USA-1647).
  const closeAfterPress = useCloseAfterPress(editing && type !== 'select')
  const close = () => closeAfterPress(() => setEditing(false))

  const commit = () => {
    if (draft.trim() === value.trim()) {
      setDraft(value)
      close()
      return
    }
    onConfirm(draft, close)
  }

  const commitSelectValue = (nextValue: string) => {
    setDraft(nextValue)
    if (nextValue === value) {
      setEditing(false)
      return
    }
    onConfirm(nextValue, () => setEditing(false))
  }

  const inputWidth = type === 'number' ? 'w-28 text-right tabular-nums' : 'w-full'

  return (
    <div
      className={`grid min-w-0 gap-1 border-b px-3 py-2 last:border-b-0 sm:grid-cols-[minmax(8rem,0.42fr)_minmax(0,1fr)] sm:items-start sm:gap-3 ${className ?? ''}`}
    >
      <Label className="pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {editing ? (
        <div>
          {type === 'select' ? (
            <Select
              open={editing}
              value={draft || undefined}
              onOpenChange={(open) => {
                if (!open) cancel()
              }}
              onValueChange={commitSelectValue}
            >
              <SelectTrigger aria-label={label} className="h-7 bg-background text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(options ?? []).map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : type === 'textarea' ? (
            <Textarea
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commit}
              onKeyDown={(event) => inlineTextareaKey(event, commit, cancel)}
              disabled={saving}
              className="min-h-20 bg-background text-sm"
            />
          ) : (
            <Input
              autoFocus
              type={type === 'number' ? 'text' : type}
              inputMode={type === 'number' ? 'decimal' : undefined}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commit}
              onKeyDown={(event) => inlineEditKey(event, commit, cancel)}
              disabled={saving}
              className={`h-7 bg-background text-sm ${inputWidth}`}
            />
          )}
        </div>
      ) : (
        <button
          type="button"
          className="block w-full rounded-md px-1.5 py-0.5 text-left text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
          onClick={startEditing}
          disabled={saving}
          title={`Click to edit ${label}`}
        >
          <span className="break-words">{displayValue ?? fieldValue(value)}</span>
        </button>
      )}
    </div>
  )
}

export function InlineChoiceControl({
  value,
  options,
  ariaLabel,
  disabled,
  styleForValue,
  onChange,
}: {
  value: string
  options: Array<{ value: string; label: string }>
  ariaLabel: string
  disabled?: boolean
  styleForValue: (value: string | null | undefined) => CSSProperties
  onChange: (value: string) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const selected = options.find((option) => option.value === value) ?? options[0]

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const stopNavigation = (event: SyntheticEvent) => {
    event.stopPropagation()
  }

  return (
    <div
      ref={containerRef}
      className="relative min-w-0"
      onClick={stopNavigation}
      onKeyDown={stopNavigation}
      onPointerDown={stopNavigation}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        style={styleForValue(value)}
      >
        <span className="truncate">{selected?.label ?? value}</span>
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-44 rounded-md border bg-popover p-1 shadow-md">
          {options.map((option) => {
            const isSelected = option.value === value
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value)
                  setIsOpen(false)
                }}
                className={`flex w-full items-center justify-between gap-2 rounded px-2.5 py-1.5 text-left text-sm ${
                  isSelected
                    ? 'bg-accent/50 text-accent-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <span>{option.label}</span>
                {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function InvoiceAssigneeControl({
  currentUser,
  assignedSource,
  saving,
  onAssign,
}: {
  currentUser?: ApInvoiceAssignedUser | null
  assignedSource?: string | null
  saving?: boolean
  onAssign: (user: ApInvoiceAssignedUser | null, done: () => void) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { data: staffList } = useStaff({ active: true })
  const staff = staffList ?? []
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const filtered = staff.filter(
    (user) =>
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase()),
  )

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (isOpen) inputRef.current?.focus()
  }, [isOpen])

  const handleSelect = (user: ApInvoiceAssignedUser | null) => {
    onAssign(user, () => {
      setIsOpen(false)
      setSearch('')
    })
  }

  return (
    <div className="relative min-w-0" ref={containerRef}>
      <button
        type="button"
        aria-label="Reassign invoice"
        onClick={() => setIsOpen((open) => !open)}
        disabled={saving}
        className={`inline-flex max-w-full items-center gap-1.5 rounded-full border pl-1 pr-2.5 text-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${
          currentUser ? 'bg-background' : 'border-dashed bg-muted/50'
        }`}
        title={
          currentUser
            ? `${currentUser.name} · ${assignedSource === 'manual' ? 'manual assignment' : 'auto assignment'}`
            : 'Assign invoice'
        }
      >
        {currentUser ? (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
            <span className="initials">{getInitials(currentUser.name)}</span>
          </span>
        ) : (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] text-muted-foreground">
            ?
          </span>
        )}
        <span className="truncate">{currentUser?.name || 'Unassigned'}</span>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-80 rounded-md border bg-popover shadow-md">
          <div className="border-b px-2 py-1.5">
            <p className="mb-1 text-xs font-medium text-muted-foreground">Assignee</p>
            <SearchInput bare
              ref={inputRef}
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search staff..."
              className="h-7 w-full rounded border bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="max-h-48 overflow-y-auto px-1 py-1">
            {currentUser && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <X className="h-3 w-3" /> Unassign
              </button>
            )}
            {filtered.map((user) => {
              const isCurrent = currentUser?.id === user.id
              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => {
                    if (!isCurrent) handleSelect(user)
                  }}
                  className={`flex w-full items-center justify-between rounded px-2.5 py-1.5 text-sm ${
                    isCurrent
                      ? 'bg-accent/50 text-accent-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary">
                      <span className="initials">{getInitials(user.name)}</span>
                    </span>
                    <span className="truncate">{user.name}</span>
                  </span>
                  {isCurrent ? (
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                  ) : (
                    <span className="shrink-0 text-xs text-muted-foreground">{user.role}</span>
                  )}
                </button>
              )
            })}
            {filtered.length === 0 && (
              <p className="px-2.5 py-3 text-center text-sm text-muted-foreground">No results</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
